import { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { cn } from "../../../lib/cn";
import { Button } from "../../shared";
import { HiOutlineCloudArrowUp, HiOutlineXMark, HiOutlineChevronDown, HiOutlineArrowPath, HiOutlineExclamationTriangle } from "react-icons/hi2";

const ALLOWED_EXTENSIONS = [
  "pdf", "md", "jpg", "jpeg", "png", "gif", "webp", "svg",
  "txt", "csv", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const QUEUE_FLASH_MS = 1600;

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatSpeed = (bytesPerSec) => {
  if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`;
  if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
  return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
};

const formatETA = (seconds) => {
  if (!seconds || !isFinite(seconds)) return "—";
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  return `${Math.ceil(seconds / 60)}m`;
};

const validateFile = (file) => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: `"${ext || "unknown"}" not supported` };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `Exceeds 100 MB limit (${formatBytes(file.size)})` };
  }
  return { valid: true };
};

const UploadQueueItem = ({ item, onRetry, onRemove }) => {
  const isUploading = item.status === "uploading";
  const isError = item.status === "error";
  const isSuccess = item.status === "success";

  return (
    <div className={cn(
      "flex flex-col gap-1.5 p-3 rounded-lg border transition-colors",
      isError ? "border-danger/30 bg-danger/5" :
      isSuccess ? "border-success/30 bg-success/5" :
      "border-surface-border bg-white"
    )}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-darkgrey truncate">{item.name}</p>
          <p className="text-xs text-grey">{formatBytes(item.size)}</p>
        </div>
        <div className="flex items-center gap-2">
          {isUploading && (
            <span className="text-xs font-medium text-secondary-dark">{item.progress}%</span>
          )}
          {isError && (
            <Button size="sm" variant="filled" color="secondary" onClick={() => onRetry(item)}>
              Retry
            </Button>
          )}
          {isSuccess && (
            <span className="text-xs font-medium text-success">Done</span>
          )}
          {(isError || isSuccess) && (
            <button
              onClick={() => onRemove(item.id)}
              className="text-grey hover:text-darkgrey p-0.5 rounded"
              aria-label="Remove"
            >
              <HiOutlineXMark className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {(isUploading || isError) && (
        <div className="w-full h-1.5 bg-surface-hover rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300",
              isError ? "bg-danger" : "bg-secondary"
            )}
            style={{ width: `${item.progress}%` }}
          />
        </div>
      )}

      {isUploading && (
        <div className="flex items-center gap-3 text-xs text-grey">
          <span>{formatSpeed(item.speed)}</span>
          <span>{formatETA(item.eta)} left</span>
        </div>
      )}

      {isError && item.error && (
        <p className="text-xs text-danger">{item.error}</p>
      )}
    </div>
  );
};

const UploadQueueTray = ({ queue, onRetry, onRemove, onDismiss, canDismiss }) => {
  const queueSummary = queue.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    },
    { uploading: 0, success: 0, error: 0 }
  );
  const activeCount = queueSummary.uploading;
  const hasFailures = queueSummary.error > 0;
  const hasSuccess = queueSummary.success > 0;
  const [openSections, setOpenSections] = useState({ uploading: true, success: false, error: true });

  useEffect(() => {
    setOpenSections((prev) => ({
      uploading: queueSummary.uploading > 0 ? true : false,
      success: queueSummary.success > 0 ? prev.success : false,
      error: queueSummary.error > 0 ? true : false,
    }));
  }, [queueSummary.uploading, queueSummary.success, queueSummary.error]);

  const sections = useMemo(
    () => [
      {
        key: "uploading",
        label: "Uploading",
        tone: "accent",
        items: queue.filter((item) => item.status === "uploading"),
      },
      {
        key: "success",
        label: "Done",
        tone: "success",
        items: queue.filter((item) => item.status === "success"),
      },
      {
        key: "error",
        label: "Failed",
        tone: "danger",
        items: queue.filter((item) => item.status === "error"),
      },
    ],
    [queue]
  );

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const retryFailed = useCallback(
    (items) => {
      items.forEach((item) => onRetry(item));
    },
    [onRetry]
  );

  return (
    <div className="fixed bottom-4 right-4 z-40 w-[min(84vw,20.5rem)] sm:bottom-6 sm:right-6 sm:w-[min(88vw,22rem)]" data-testid="upload-queue-tray">
      <div className="overflow-hidden rounded-xl border border-surface-border bg-white shadow-xl">
        <div className="border-b border-surface-border px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-darkgrey">Upload queue</p>
              {activeCount > 0 ? (
                <p className="text-xs text-grey">{queueSummary.uploading} uploading</p>
              ) : hasFailures ? (
                <p className="text-xs text-grey">{queueSummary.error} failed</p>
              ) : hasSuccess ? (
                <p className="text-xs text-grey">Completed</p>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={onDismiss}
                disabled={!canDismiss}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-surface-border text-grey hover:bg-surface-hover hover:text-darkgrey disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Dismiss upload queue"
              >
                <HiOutlineXMark className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-[min(18rem,calc(100vh-7rem))] overflow-auto p-2.5">
          <div className="space-y-2">
            {sections.filter((section) => section.items.length > 0).map((section) => {
              const isOpen = openSections[section.key];
              const hasItems = section.items.length > 0;
              return (
                <div
                  key={section.key}
                  className={cn(
                    "overflow-hidden rounded-lg border",
                    section.tone === "success"
                      ? "border-success/20 bg-success/5"
                      : section.tone === "danger"
                      ? "border-danger/20 bg-danger/5"
                      : section.tone === "accent"
                      ? "border-secondary/20 bg-secondary/5"
                      : "border-surface-border bg-surface/50"
                  )}
                >
                  <div className="flex items-center justify-between gap-2.5 px-2.5 py-2">
                    <button
                      type="button"
                      onClick={() => toggleSection(section.key)}
                      className="flex min-w-0 flex-1 items-center justify-between gap-2.5 text-left disabled:cursor-default"
                      aria-expanded={isOpen}
                      disabled={!hasItems}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-darkgrey">{section.label}</p>
                          <span className="rounded-full border border-surface-border bg-white px-1.5 py-0.5 text-[10px] font-semibold text-grey">
                            {section.items.length}
                          </span>
                        </div>
                        {section.key === "error" && section.items.length > 0 && (
                          <p className="mt-0.5 text-[11px] text-grey">Retry failed uploads</p>
                        )}
                      </div>

                      <HiOutlineChevronDown
                        className={cn(
                          "h-4 w-4 shrink-0 text-grey transition-transform",
                          isOpen ? "rotate-180" : "rotate-0"
                        )}
                      />
                    </button>

                    <div className="flex shrink-0 items-center gap-1.5">
                      {section.key === "error" && section.items.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            retryFailed(section.items);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-danger/20 bg-white px-2 py-1 text-[11px] font-medium text-danger hover:bg-danger/5"
                        >
                          <HiOutlineArrowPath className="h-3 w-3" />
                          Retry all
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen && hasItems && (
                    <div className="space-y-2 px-2.5 pb-2.5">
                      {section.items.map((item) => (
                        <UploadQueueItem
                          key={item.id}
                          item={item}
                          onRetry={onRetry}
                          onRemove={onRemove}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FileUploader = forwardRef(({
  queue = [],
  isUploading,
  onEnqueue,
  onRetry,
  onRemove,
  onClearCompleted,
  canUpload,
  usagePercent = 0,
}, ref) => {
  const dropRef = useRef(null);
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [queueFlash, setQueueFlash] = useState(false);
  const [isQueueDismissed, setIsQueueDismissed] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const previousQueueLengthRef = useRef(queue.length);
  useImperativeHandle(ref, () => ({
    click: () => inputRef.current?.click(),
  }));

  useEffect(() => {
    if (queue.length > previousQueueLengthRef.current) {
      setIsQueueDismissed(false);
      setQueueFlash(true);
      const timer = window.setTimeout(() => setQueueFlash(false), QUEUE_FLASH_MS);
      previousQueueLengthRef.current = queue.length;
      return () => window.clearTimeout(timer);
    }
    previousQueueLengthRef.current = queue.length;
    return undefined;
  }, [queue.length]);

  // Auto-close the queue 500ms after every upload finishes successfully.
  // Clearing completed items empties the queue so the tray unmounts; it
  // re-appears automatically when new files are enqueued.
  const activeCount = queue.filter(
    (item) => item.status === "pending" || item.status === "uploading"
  ).length;
  const errorCount = queue.filter((item) => item.status === "error").length;
  const successCount = queue.filter((item) => item.status === "success").length;

  useEffect(() => {
    if (activeCount === 0 && errorCount === 0 && successCount > 0) {
      const timer = window.setTimeout(() => onClearCompleted?.(), 500);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [activeCount, errorCount, successCount, onClearCompleted]);

  useEffect(() => {
    if (queue.length === 0) {
      setIsQueueDismissed(false);
    }
  }, [queue.length]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const processFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList);
      const validated = files.map((f) => ({ file: f, ...validateFile(f) }));
      const valid = validated.filter((v) => v.valid);
      const invalid = validated.filter((v) => !v.valid);

      if (invalid.length) {
        const rejected = invalid.map((v) => ({
          name: v.file.name,
          error: v.error,
        }));
        setRejectedFiles((prev) => [...prev, ...rejected]);
      }

      if (valid.length) {
        onEnqueue(valid.map((v) => v.file));
      }
    },
    [onEnqueue]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (!canUpload || usagePercent >= 100) return;
      if (e.dataTransfer?.files?.length) processFiles(e.dataTransfer.files);
    },
    [canUpload, usagePercent, processFiles]
  );

  const handleInputChange = useCallback(
    (e) => {
      if (e.target.files?.length) processFiles(e.target.files);
      e.target.value = "";
    },
    [processFiles]
  );

  if (!canUpload) return null;

  const isFull = usagePercent >= 100;

  const showQueueTray = queue.length > 0 && !isQueueDismissed;

  return (
    <div className="w-full">
      <div
        ref={dropRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer h-full flex flex-col items-center justify-center",
          queueFlash && "ring-2 ring-secondary/20 bg-secondary/[0.03] shadow-[0_0_0_4px_rgba(14,165,233,0.08)] animate-pulse motion-reduce:animate-none",
          isDragOver
            ? "border-secondary bg-secondary/5 shadow-[0_0_0_4px_rgba(56,189,248,0.12)] scale-[1.01]"
            : isFull
            ? "border-danger/30 bg-danger/5 cursor-not-allowed"
            : "border-surface-border bg-white hover:border-primary/40 hover:bg-primary/[0.02]"
        )}
        data-testid="upload-dropzone"
        onClick={() => !isFull && inputRef.current?.click()}
        role="button"
        aria-label="Upload files"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !isFull) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(",")}
          className="hidden"
          onChange={handleInputChange}
          disabled={isFull || isUploading}
        />

        {isFull ? (
          <div className="flex flex-col items-center gap-2">
            <HiOutlineExclamationTriangle className="w-8 h-8 text-danger" aria-hidden="true" />
            <p className="text-sm font-medium text-danger">Storage full</p>
            <p className="text-xs text-grey">
              Remove files or contact your project manager
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <HiOutlineCloudArrowUp className={cn("w-10 h-10 transition-all duration-200", isDragOver ? "text-secondary-dark animate-pulse" : "text-secondary-dark")} />
            <p className="text-sm font-medium text-darkgrey">
              {isDragOver ? "Drop files here" : "Drag & drop files here"}
            </p>
            <p className="text-sm text-darkgrey">
              or <span className="text-secondary-dark font-medium">Browse files</span>
            </p>
            <p className="text-xs text-grey">
              PDF, images, documents up to 100 MB
            </p>
          </div>
        )}
      </div>

      {rejectedFiles.length > 0 && (
        <div className="flex flex-col gap-1 p-3 rounded-lg border border-amber/30 bg-amber/5">
          {rejectedFiles.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <p className="text-xs text-darkgrey truncate">{r.name}</p>
              <p className="text-xs text-warning whitespace-nowrap">{r.error}</p>
            </div>
          ))}
        </div>
      )}

      {showQueueTray && (
        <UploadQueueTray
          queue={queue}
          onRetry={onRetry}
          onRemove={onRemove}
          onDismiss={() => setIsQueueDismissed(true)}
          canDismiss={activeCount === 0}
        />
      )}
    </div>
  );
});

FileUploader.displayName = "FileUploader";
